import { useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import useCustomerAddress from '../../hooks/useCustomerAddress'
import { desktopVw } from '../../styles/utils'
import { mobileNumberPattern } from '../../utils/validation'
import Form from '../Form'
import Modal from '../Modal'
import { captureException } from '../Sentry/utils'
import { useTranslation } from 'next-i18next'
import { parseLocale } from '@src/utils/i18n.utils'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { getAddressFieldMaxLength } from '@src/quince-site-common/src/services/addressService/address-field-limits.utils'

export const CONTENTFUL_TO_SP_PROVINCE_MAP = {
  'Washington, District of Columbia': 'DC',
}

function getSpAddress(address) {
  return {
    ...address,
    province: CONTENTFUL_TO_SP_PROVINCE_MAP[address.province] || address.province,
  }
}

const StyledModal = styled(Modal)`
  && {
    position: relative;
    overflow-y: scroll;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    padding: ${desktopVw(44)} ${desktopVw(30)} ${desktopVw(84)};
    margin-top: ${desktopVw(64)};
    border-radius: ${desktopVw(16)} ${desktopVw(16)} 0px 0px;
    max-height: 90vh;

    @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
      padding: ${desktopVw(48)};
      height: auto;
      max-height: 90vh;
      width: ${desktopVw(712)};
      box-sizing: border-box;
      margin-top: unset;
      border-radius: 0;

      .container__half {
        vertical-align: top;
      }
    }
  }
`

const ErrorMsg = styled.p`
  font-family: ${props => props.theme.fonts.primary};
  font-size: 14px;
  margin: 10px 0 0;
  margin-bottom: 0 !important;
  color: red;
  text-align: center;

  @media (min-width: ${props => props.theme.breakpoints.desktopSmall}px) {
    margin: ${desktopVw(10)} 0 0;
    font-size: ${desktopVw(14)};
  }
`

const getTransformedFormFields = (formFields, t, stateOptions) => {
  const stateNames = stateOptions?.map(state => state.stateName)
  return (formFields || []).map(cf => {
    const maxLength = getAddressFieldMaxLength(cf?.name)
    const fieldWithLength = maxLength ? { ...cf, maxLength } : cf

    if (cf?.name === 'province') {
      return {
        ...fieldWithLength,
        options: stateNames,
      }
    }
    if (cf?.name === 'phone') {
      return {
        ...fieldWithLength,
        pattern: mobileNumberPattern,
        // eslint-disable-next-line i18next/no-literal-string
        patternMessage: t('valid_phone_number_required'),
      }
    }
    return fieldWithLength
  })
}

// @i18n-ignore
const US_COUNTRY_CODE = 'US'
// TODO-LOCALIZATION: Add translation for this after confirming if this need to be translated
const US_COUNTRY_LABEL = 'United States'

const updatedFormValues = (formValues, stateOptions) => {
  return {
    ...formValues,
    country: formValues.country === US_COUNTRY_CODE ? US_COUNTRY_LABEL : formValues.country,
    province: stateOptions.find(state => state.stateCode === formValues.province)?.stateName || formValues.province,
  }
}

const AddressModal = ({
  open,
  closeModal,
  addressForm,
  refreshCustomerAddress,
  initialFormData,
  mode,
  id,
  customerId,
  sendAddressAddtionSuccessfulEvent,
  sendAddressEditSuccessfulEvent,
  stateOptions,
  isDefault,
  skipProvinceInAddress,
}) => {
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: updatedFormValues(initialFormData, stateOptions),
  })
  const { t } = useTranslation('accountPage')
  const { t: tCommon } = useTranslation('common')
  const formValues = getValues()
  const [errorMsg, setErrorMsg] = useState('')
  const [defaultAddressErrorMsg, setDefaultAddressErrorMsg] = useState('')
  const [isDefaultAddressError, setIsDefaultAddressError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [defaultAddress, setDefaultAddress] = useState(isDefault)
  const isDefaultAddress = isDefault || defaultAddress

  const { locale } = useLocaleContext()
  const { country } = parseLocale(locale)

  const {
    newCreateAddress,
    newUpdateAddress,
    validateAddress,
    apiResponseErrors,
    validateZipCodeStateCombination,
    validatePostalCode,
  } = useCustomerAddress(stateOptions)

  const newResponseHandler = ({ response }) => {
    if (response) {
      setIsDefaultAddressError(false)
      setErrorMsg('')
      setDefaultAddressErrorMsg('')
      setLoading(false)
      closeModal()
      refreshCustomerAddress()
      reset(updatedFormValues(initialFormData, stateOptions))
      window.scrollTo(0, 0)
    }
  }

  const handleNewAddressFlow = async data => {
    try {
      const validationFn = skipProvinceInAddress ? validatePostalCode : validateZipCodeStateCombination
      const validationResponse = await validationFn(data)
      if (validationResponse.success === false) {
        return
      }
      const response = await validateAddress(data, t)
      if (!response || Object.keys(response).length === 0) {
        setErrorMsg(t('validateAddressErr'))
        return
      }
      if (response.hasGeneralInvalidAddressCode) {
        setErrorMsg(t('generalAddressErr'))
      }

      if (response.isAddressValid) {
        const enrichedAddress = response.enrichedAddress ?? null
        if (mode === 'add') {
          sendAddressAddtionSuccessfulEvent?.()
          newCreateAddress(data, isDefaultAddress, enrichedAddress).then(newResponseHandler)
        } else if (mode === 'edit') {
          sendAddressEditSuccessfulEvent?.()
          newUpdateAddress(id, customerId, data, isDefaultAddress, enrichedAddress).then(newResponseHandler)
        }
        reset()
      }
    } catch (e) {
      captureException(new Error(`Failed to ${mode} the address for the user`))
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = baseAddress => {
    const address = getSpAddress(baseAddress)
    setLoading(true)
    address.country = country

    handleNewAddressFlow(address)
  }

  const onDefaultAddressChange = () => {
    setDefaultAddress(prevState => !prevState)
  }

  return (
    <StyledModal
      open={open}
      onClose={() => {
        reset(updatedFormValues(initialFormData, stateOptions))
        setErrorMsg('')
        closeModal()
      }}
    >
      <Form
        fields={getTransformedFormFields(addressForm.fields, tCommon, stateOptions)}
        title={addressForm.title}
        submitButtonText={mode === 'add' ? t('saveAddress') : t('updateAddress')}
        handleSubmit={handleSubmit(onSubmit)}
        register={register}
        formValues={formValues}
        errors={{ ...errors, ...apiResponseErrors }}
        loading={loading}
        defaultAddressCheckbox
        defaultAddress={defaultAddress}
        onCheckboxChange={onDefaultAddressChange}
      />
      {errorMsg && <ErrorMsg>{errorMsg}</ErrorMsg>}
      {isDefaultAddressError && <ErrorMsg>{defaultAddressErrorMsg}</ErrorMsg>}
    </StyledModal>
  )
}

export default AddressModal
